import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, MessageCircle, Volume2, VolumeX, Eye, EyeOff, Settings, Phone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

import { personalityTypes } from '@/data/personalityData';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useSilenceTimer } from '@/hooks/useSilenceTimer';
import ChatMessages from '@/components/training/ChatMessages';
import ChatInput from '@/components/training/ChatInput';
import PersonalitySelector from '@/components/training/PersonalitySelector';
import SessionStatus from '@/components/training/SessionStatus';
import ScenarioInfo from '@/components/training/ScenarioInfo';
import TrainingTips from '@/components/training/TrainingTips';
import DispatcherChecklist from '@/components/training/DispatcherChecklist';
import ProtocolPanel from '@/components/health/ProtocolPanel';
import PhoneCallMode from '@/components/training/PhoneCallMode';

const TrainingChat = ({ studentInfo }) => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();

  // Auto-set operator name from student profile
  const studentFullName = studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : 'Répartiteur';

  // Core state
  const [scenario, setScenario] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [operatorName] = useState(studentFullName);
  const [showPersonalitySelect, setShowPersonalitySelect] = useState(true);
  const [selectedPersonality, setSelectedPersonality] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [showScenario, setShowScenario] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [hideCitizenText, setHideCitizenText] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentHint, setCurrentHint] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [hintsTotal, setHintsTotal] = useState(0);
  const [allHintsUsed, setAllHintsUsed] = useState(false);
  const [showPhoneCall, setShowPhoneCall] = useState(false);
  const [trainingLanguage, setTrainingLanguage] = useState(() => {
    return sessionStorage.getItem('selected_training_language') || 'french';
  });

  // Refs for reliable closure access
  const durationInterval = useRef(null);
  const isSendingRef = useRef(false);
  const sessionRef = useRef(null);
  const personalityRef = useRef(null);
  const scenarioRef = useRef(null);
  const trainingLanguageRef = useRef(trainingLanguage);
  const isSessionActiveRef = useRef(false);
  const messageInputRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { personalityRef.current = selectedPersonality; }, [selectedPersonality]);
  useEffect(() => { scenarioRef.current = scenario; }, [scenario]);
  useEffect(() => { trainingLanguageRef.current = trainingLanguage; }, [trainingLanguage]);
  useEffect(() => { isSessionActiveRef.current = isSessionActive; }, [isSessionActive]);

  // Hooks
  const speech = useSpeechRecognition();

  const onAiMessage = useCallback((msg) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  const silenceTimer = useSilenceTimer({
    sessionRef, personalityRef, trainingLanguageRef, scenarioRef,
    isSessionActiveRef, isSendingRef, onAiMessage
  });

  // Initialize speech recognition
  useEffect(() => {
    speech.initialize();
    return () => {
      speech.cleanup();
      window.speechSynthesis?.cancel();
    };
  }, []);

  // TTS: Get best voice for the training language
  const getVoiceForLanguage = (lang) => {
    const voices = window.speechSynthesis?.getVoices() || [];
    if (lang === 'english') {
      return voices.find(v => v.lang === 'en-CA')
        || voices.find(v => v.lang === 'en-US')
        || voices.find(v => v.lang.startsWith('en'))
        || null;
    }
    return voices.find(v => v.lang === 'fr-CA') 
      || voices.find(v => v.lang.startsWith('fr')) 
      || null;
  };

  // TTS: Speak citizen line
  // Format text for better TTS pronunciation
  const digitToFrench = { '0': 'zéro', '1': 'un', '2': 'deux', '3': 'trois', '4': 'quatre', '5': 'cinq', '6': 'six', '7': 'sept', '8': 'huit', '9': 'neuf' };

  const formatTextForTTS = (text) => {
    return text.replace(/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, (match) => {
      const digits = match.replace(/\D/g, '');
      const part1 = digits.slice(0, 3).split('').map(d => digitToFrench[d]).join(' ');
      const part2 = digits.slice(3, 6).split('').map(d => digitToFrench[d]).join(' ');
      const part3 = digits.slice(6).split('').map(d => digitToFrench[d]).join(' ');
      return part1 + ', ' + part2 + ', ' + part3;
    }).replace(/\b(\d+)\b/g, (match) => {
      if (match.length <= 4) {
        return match.split('').map(d => digitToFrench[d] || d).join(' ');
      }
      return match;
    });
  };

  // Stable reference to silence timer start
  const silenceTimerStartRef = useRef(silenceTimer.start);
  useEffect(() => { silenceTimerStartRef.current = silenceTimer.start; }, [silenceTimer.start]);

  // TTS: Auto-speak new citizen messages + restart silence timer after speech ends
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.speaker === 'citizen') {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (ttsEnabled && window.speechSynthesis && !isMobile) {
          // Desktop + TTS on: speak, then start silence timer AFTER speech finishes
          try { window.speechSynthesis.cancel(); } catch(e) { /* ignore */ }
          const utterance = new SpeechSynthesisUtterance(formatTextForTTS(lastMsg.text));
          const ttsLang = trainingLanguage === 'english' ? 'en-CA' : 'fr-CA';
          utterance.lang = ttsLang;
          const voice = getVoiceForLanguage(trainingLanguage);
          if (voice) utterance.voice = voice;
          utterance.rate = 1.0;
          utterance.pitch = 1.0;

          let timerStarted = false;
          const startTimerOnce = () => {
            if (!timerStarted) {
              timerStarted = true;
              silenceTimerStartRef.current();
            }
          };

          utterance.onend = startTimerOnce;
          utterance.onerror = startTimerOnce;

          // Desktop safety: very generous timeout (60s) in case onend never fires
          const safetyTimeout = setTimeout(startTimerOnce, 60000);
          utterance.onend = () => { clearTimeout(safetyTimeout); startTimerOnce(); };
          utterance.onerror = () => { clearTimeout(safetyTimeout); startTimerOnce(); };

          try { window.speechSynthesis.speak(utterance); } catch(e) { startTimerOnce(); }
        } else if (ttsEnabled && isMobile) {
          // Mobile + TTS on: fire-and-forget TTS, start timer immediately
          try {
            window.speechSynthesis?.cancel();
            const utterance = new SpeechSynthesisUtterance(formatTextForTTS(lastMsg.text));
            const ttsLang = trainingLanguage === 'english' ? 'en-CA' : 'fr-CA';
            utterance.lang = ttsLang;
            const voice = getVoiceForLanguage(trainingLanguage);
            if (voice) utterance.voice = voice;
            window.speechSynthesis.speak(utterance);
          } catch(e) { /* ignore */ }
          // iOS keepalive
          if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            const keepAlive = setInterval(() => {
              if (!window.speechSynthesis?.speaking) { clearInterval(keepAlive); return; }
              try { window.speechSynthesis.resume(); } catch(e) { /* ignore */ }
            }, 5000);
          }
          silenceTimerStartRef.current();
        } else {
          // TTS off: start silence timer immediately
          silenceTimerStartRef.current();
        }
      }
    }
  // eslint-disable-next-line
  }, [messages, ttsEnabled, trainingLanguage]);

  const toggleTts = () => {
    const newVal = !ttsEnabled;
    setTtsEnabled(newVal);
    if (!newVal) {
      setHideCitizenText(false);
      try { window.speechSynthesis?.cancel(); } catch(e) { /* ignore */ }
    }
  };

  // Auto-focus input when citizen replies
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].speaker === 'citizen' && isSessionActive) {
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 500);
    }
  }, [messages, isSessionActive]);

  // Main initialization — detect call mode and load scenario
  useEffect(() => {
    const selectedLang = sessionStorage.getItem('selected_training_language');
    if (selectedLang) {
      setTrainingLanguage(selectedLang);
      if (selectedLang === 'english') {
        document.documentElement.setAttribute('lang', 'en');
        document.body.classList.add('notranslate');
      } else {
        document.documentElement.setAttribute('lang', 'fr');
        document.body.classList.remove('notranslate');
      }
    }

    const isRandomCall = sessionStorage.getItem('is_random_call') === 'true';
    const isAssignedCall = sessionStorage.getItem('assigned_call_mode') === 'true';

    if (isRandomCall) {
      handleRandomCallInit();
    } else if (isAssignedCall) {
      handleAssignedCallInit();
    } else {
      fetchScenario();
    }

    return () => {
      if (durationInterval.current) clearInterval(durationInterval.current);
      silenceTimer.clear();
    };
  }, [scenarioId]);

  // ---- Session initialization functions ----

  const fetchScenario = async () => {
    try {
      const response = await axios.get(`${API}/scenarios/${scenarioId}`);
      setScenario(response.data);
    } catch (error) {
      console.error('Error fetching scenario:', error);
      toast.error('Erreur lors du chargement du scénario');
      navigate('/scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleRandomCallInit = () => {
    const savedScenario = sessionStorage.getItem('random_call_scenario');
    const savedPersonality = sessionStorage.getItem('random_call_personality');

    if (savedScenario && savedPersonality) {
      const parsed = JSON.parse(savedScenario);
      setScenario(parsed);
      setTrainingLanguage(parsed.language || 'french');

      const personality = personalityTypes.find(p => p.id === savedPersonality);
      if (personality) setSelectedPersonality(personality);

      setShowPersonalitySelect(false);

      setTimeout(() => createAndStartSession(parsed, studentFullName), 100);

      sessionStorage.removeItem('random_call_scenario');
      sessionStorage.removeItem('random_call_personality');
      sessionStorage.removeItem('random_call_operator');
      sessionStorage.removeItem('is_random_call');
    }
    setLoading(false);
  };

  const handleAssignedCallInit = () => {
    const savedScenario = sessionStorage.getItem('assigned_call_scenario');
    const savedPersonality = sessionStorage.getItem('assigned_call_personality');
    const savedLanguage = sessionStorage.getItem('selected_training_language');

    if (savedScenario && savedPersonality) {
      const parsed = JSON.parse(savedScenario);
      setScenario(parsed);
      if (savedLanguage) setTrainingLanguage(savedLanguage);

      const personality = personalityTypes.find(p => p.id === savedPersonality);
      if (personality) setSelectedPersonality(personality);

      setShowPersonalitySelect(false);

      setTimeout(() => createAndStartSession(parsed, studentFullName), 100);

      sessionStorage.removeItem('assigned_call_scenario');
      sessionStorage.removeItem('assigned_call_personality');
      sessionStorage.removeItem('assigned_call_mode');
    } else {
      fetchScenario();
    }
    setLoading(false);
  };

  const createAndStartSession = async (scenarioData, operatorNameValue) => {
    try {
      const response = await axios.post(`${API}/sessions`, {
        scenario_id: scenarioData.id,
        operator_name: operatorNameValue,
        student_number: studentInfo?.student_number || null
      });
      setSession(response.data);
      setIsSessionActive(true);
      setMessages([]);

      durationInterval.current = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);

      toast.success('Appel 911 - Répondez maintenant!', { duration: 3000 });
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Erreur lors du démarrage de la session');
    }
  };

  // ---- User actions ----

  const proceedToPersonalitySelection = () => {
    // No longer needed - personality is shown directly
  };

  const startSession = async () => {
    if (!selectedPersonality) {
      toast.error('Veuillez choisir une personnalité de citoyen');
      return;
    }
    try {
      const response = await axios.post(`${API}/sessions`, {
        scenario_id: scenarioId,
        operator_name: operatorName,
        student_number: studentInfo?.student_number || null
      });
      setSession(response.data);
      setShowPersonalitySelect(false);
      setIsSessionActive(true);

      durationInterval.current = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);

      setMessages([]);
      toast.success("Session démarrée - Répondez à l'appel en tant que répartiteur!");
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Erreur lors du démarrage de la session');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    silenceTimer.clear();

    if (!currentMessage.trim() || sending || isSendingRef.current || !session || !selectedPersonality) return;

    if (speech.isListening) speech.stopListening();

    setSending(true);
    isSendingRef.current = true;

    const userMessage = { speaker: 'operator', text: currentMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = currentMessage;
    setCurrentMessage('');

    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: session.id,
        message: messageToSend,
        scenario_context: scenario,
        personality: selectedPersonality.id,
        language: trainingLanguage
      });

      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }

      const aiMessage = { speaker: 'citizen', text: response.data.response, timestamp: response.data.timestamp };
      setMessages(prev => [...prev, aiMessage]);
      // Note: silence timer is started via the auto-speak useEffect (after TTS ends if enabled)
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
      isSendingRef.current = false;
    }
  };

  const toggleScenario = async (show) => {
    setShowScenario(show);
    if (show && isSessionActive && session) {
      try {
        await axios.post(`${API}/sessions/${session.id}/viewed-scenario`);
      } catch (error) {
        console.error('Error recording scenario view:', error);
      }
    }
  };

  const endSession = async () => {
    if (durationInterval.current) clearInterval(durationInterval.current);
    silenceTimer.clear();
    setIsSessionActive(false);

    if (session) {
      try {
        await axios.put(`${API}/sessions/${session.id}`, {
          end_time: new Date().toISOString(),
          duration_seconds: sessionDuration,
          status: 'completed'
        });

        const assignedCallId = sessionStorage.getItem('assigned_call_id');
        if (assignedCallId) {
          await axios.put(`${API}/assigned-calls/${assignedCallId}`, {
            status: 'completed',
            session_id: session.id
          });
          sessionStorage.removeItem('assigned_call_id');
          sessionStorage.removeItem('assigned_call_mode');
        }

        toast.success('Session terminée avec succès');

        // Always redirect to history - retroaction is on-demand
        setTimeout(() => navigate('/history'), 1500);
      } catch (error) {
        console.error('Error ending session:', error);
        toast.error('Erreur lors de la fin de session');
      }
    }
  };

  const handleToggleListening = () => {
    speech.toggleListening((transcript) => setCurrentMessage(transcript));
  };

  // 3QPOC: record when the student opens the checklist
  const handle3qpocOpen = useCallback(() => {
    if (session?.id) {
      axios.post(`${API}/sessions/${session.id}/used-3qpoc`).catch(() => {});
    }
  }, [session]);

  // Hint system: request next hint
  const handleRequestHint = useCallback(async () => {
    if (!session?.id) return null;
    try {
      const resp = await axios.post(`${API}/sessions/${session.id}/hint`);
      const data = resp.data;
      if (data.hint) {
        setCurrentHint(data.hint);
        setHintsUsed(data.hints_used);
        setHintsTotal(data.hints_total);
        setAllHintsUsed(false);
        return data.hint;
      } else {
        setAllHintsUsed(true);
        toast.info(data.message || 'Tous les indices ont été utilisés.');
        return null;
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la récupération de l\'indice';
      toast.error(msg);
      return null;
    }
  }, [session]);

  // Check if hints are available for this scenario (incendie)
  const isHintAvailable = scenario && scenario.category === 'incendie';

  // ---- Render ----

  if (loading) {
    return (
      <div data-testid="training-loading" className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-xl" style={{ color: '#2E6CF5' }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div data-testid="training-chat-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-2">Formation Vocale + Chat</h1>
        <p className="text-lg text-gray-600">Parlez naturellement, l'IA répond par écrit</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-1">
          <ScenarioInfo scenario={scenario} showScenario={showScenario} onToggle={toggleScenario} />
          
          {/* Settings Panel - TTS toggles */}
          <Card className="glass border-0 mb-6">
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
              onClick={() => setShowSettings(!showSettings)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>Paramètres</span>
                </CardTitle>
                <span className="text-gray-400 text-sm">{showSettings ? '▲' : '▼'}</span>
              </div>
            </CardHeader>
            {showSettings && (
              <CardContent className="space-y-4 pt-0">
                {/* TTS Toggle */}
                <div>
                  <label className="flex items-center justify-between text-sm font-medium text-gray-700" data-testid="chat-tts-toggle-label">
                    <div className="flex items-center space-x-2">
                      {ttsEnabled ? <Volume2 className="w-4 h-4" style={{ color: '#2E6CF5' }} /> : <VolumeX className="w-4 h-4 text-gray-400" />}
                      <span>Voix du citoyen</span>
                    </div>
                    <button
                      onClick={toggleTts}
                      data-testid="chat-tts-btn"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        ttsEnabled ? '' : 'bg-gray-300'
                      }`}
                      style={ttsEnabled ? { backgroundColor: '#2E6CF5' } : {}}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        ttsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Le système lira les réponses du citoyen à voix haute</p>
                </div>

                {/* Hide citizen text toggle */}
                {ttsEnabled && (
                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700" data-testid="chat-hide-text-label">
                      <div className="flex items-center space-x-2">
                        {hideCitizenText ? <EyeOff className="w-4 h-4" style={{ color: '#EE2130' }} /> : <Eye className="w-4 h-4 text-gray-400" />}
                        <span>Masquer texte citoyen</span>
                      </div>
                      <button
                        onClick={() => setHideCitizenText(!hideCitizenText)}
                        data-testid="chat-hide-text-btn"
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          hideCitizenText ? '' : 'bg-gray-300'
                        }`}
                        style={hideCitizenText ? { backgroundColor: '#EE2130' } : {}}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          hideCitizenText ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Mode réaliste : entendez sans voir le texte</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {isSessionActive && (
            <>
              {scenario?.category === 'ambulance' && studentInfo?.permissions?.ambulance ? (
                <ProtocolPanel sessionId={session?.id} />
              ) : (
                <DispatcherChecklist onOpen={handle3qpocOpen} />
              )}
              <SessionStatus sessionDuration={sessionDuration} onEndSession={endSession} />
            </>
          )}
        </div>

        {/* Right Columns */}
        <div className="lg:col-span-2">
          {showPersonalitySelect ? (
            <PersonalitySelector
              personalityTypes={personalityTypes}
              selectedPersonality={selectedPersonality}
              setSelectedPersonality={setSelectedPersonality}
              onStart={startSession}
              onBack={() => navigate(-1)}
            />
          ) : (
            <Card className="glass border-0 h-[600px] flex flex-col">
              <CardHeader className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <MessageCircle className="w-5 h-5" />
                      <span>Conversation d'Urgence</span>
                    </CardTitle>
                    <CardDescription>Vous communiquez avec un citoyen en détresse</CardDescription>
                  </div>
                  {isSessionActive && (
                    <button
                      type="button"
                      onClick={() => setShowPhoneCall(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                      style={{ background: 'linear-gradient(135deg, #002768 0%, #2E6CF5 100%)' }}
                      data-testid="phone-call-mode-btn"
                      title="Conversation mains libres avec interruption (mode téléphone)"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="hidden sm:inline">Mode Téléphone</span>
                    </button>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex-1 overflow-hidden flex p-4 space-x-4">
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4" data-testid="messages-container" translate="no">
                    <ChatMessages messages={messages} scenario={scenario} ttsEnabled={ttsEnabled} hideCitizenText={hideCitizenText} />
                  </div>

                  <ChatInput
                    currentMessage={currentMessage}
                    setCurrentMessage={setCurrentMessage}
                    onSend={sendMessage}
                    sending={sending}
                    isListening={speech.isListening}
                    onToggleListening={handleToggleListening}
                    isSessionActive={isSessionActive}
                    hintState={isHintAvailable ? {
                      available: true,
                      currentHint,
                      hintsUsed,
                      hintsTotal,
                      allUsed: allHintsUsed,
                      onRequestHint: handleRequestHint,
                    } : null}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <TrainingTips showTips={showTips} setShowTips={setShowTips} />

      <PhoneCallMode
        open={showPhoneCall}
        onClose={() => setShowPhoneCall(false)}
        sessionId={session?.id}
        scenario={scenario}
        personality={selectedPersonality?.id || 'panicked'}
        language={trainingLanguage}
        onMessages={(speaker, text) => {
          if (!text || !text.trim()) return;
          // Map phone-mode speaker labels to the format expected by ChatMessages
          const mapped = speaker === 'dispatcher' ? 'operator' : 'citizen';
          setMessages(prev => [...prev, { speaker: mapped, text, timestamp: new Date().toISOString() }]);
        }}
      />
    </div>
  );
};

export default TrainingChat;
